import type * as React from 'react';
import {useState, useCallback, useMemo} from 'react';
import type {Item, RegisterableCollection} from './types';
import {
  useIndexedChildren,
  compareIndexPaths,
  treeFromItems,
} from './useIndexedChildren';

/**
 * This is the starting point for creating a component that uses collections.
 * Use this in the top-level component to create the data structure that holds
 * items. `useItem` is complementary to this and will be used inside item
 * components to register items.
 */
export function useRegisterableCollection(
  children: React.ReactNode,
): RegisterableCollection {
  const [version, setVersion] = useState(Math.random());
  const itemMap = useMemo(() => new Map<string, Item | undefined>(), []);

  const register = useCallback(
    (key: string, item: Item) => {
      itemMap.set(key, item);
      setVersion(Math.random());
      return () => {
        // Instead of deleting the item, we set it to undefined to keep the original order that the items were registered in
        itemMap.set(key, undefined);
        setVersion(Math.random());
      };
    },
    [itemMap],
  );

  const getItems = useMemo(
    // TODO: We should filter results using this filter function. But we don't have any userItem, since we don't receive a data array here like in useListCollectionWithData
    //        I think we could remove the userItem param from the filter function, I didn't seen any usage of it in the codebase.
    <T>(_filter?: (item: Item, userItem: T) => boolean) => {
      let cache: Item[] | null = null;

      return () => {
        if (cache) {
          return cache;
        }

        // We trick React into thinking we depend on `version` so we
        // can control when `getItems` is recreated. We don't recreate
        // `itemMap` so we can't use it as a signal to recreate
        // `getItems`. The goal is to avoid constantly recreating
        // the `Map` for `itemMap`.
        //
        // eslint-disable-next-line no-unused-expressions
        version;

        const items = Array.from(itemMap.entries())
          .filter((pair): pair is [string, Item] => pair[1] !== undefined)
          .sort((a, b) =>
            compareIndexPaths(a[1].indexPathString, b[1].indexPathString),
          )
          .map((entry) => entry[1]);

        cache = treeFromItems(items);
        return cache;
      };
    },
    [itemMap, version],
  );

  const indexedChildren = useIndexedChildren(children);

  return {
    register,
    getItems,
    render() {
      return indexedChildren;
    },
  };
}
